import { memo, useRef } from 'react';

import { pluralize } from '@/utils';
import { Input } from '@/shadcn/input';
import { Button } from '@/shadcn/button';
import { TestId } from '@/types/testId';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';

import { WorkflowGroupsQueryQuery } from '@/apollo/graphql';

type Props = {
  workflow: WorkflowGroupsQueryQuery['workflowByRowId'];
  searchGroups: string;
  updateSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  openAddGroupModal: () => void;
  isLoading: boolean;
};

export const SubHeader = memo(
  ({
    workflow,
    searchGroups,
    updateSearch,
    openAddGroupModal,
    isLoading
  }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useKeyboardShortcut(inputRef, {
      key: 'f',
      ctrlKey: true,
      preventDefault: true
    });

    const {
      nonFilteredGroups: { totalCount: nbNonFilteredGroups } = {},
      groups: { totalCount: nbFilteredGroups } = {}
    } = workflow || {};

    let groupsCount = 'No groups added yet';
    if (nbNonFilteredGroups) {
      const totalGroupsCount = pluralize(
        nbNonFilteredGroups,
        'group',
        false,
        true
      );
      if (searchGroups) {
        groupsCount = `${nbFilteredGroups} of ${totalGroupsCount}`;
      } else {
        groupsCount = totalGroupsCount;
      }
    }

    return (
      <div className="border-border bg-card flex shrink-0 flex-row flex-nowrap items-center justify-between border-b px-5 py-4">
        <div className="flex flex-col flex-nowrap items-start sm:flex-row sm:items-center">
          <div className="mr-5 text-2xl font-semibold">Groups settings</div>
          <div className="mt-2.5 sm:mt-0">
            <div className="*:not-first:mt-2">
              <div className="relative">
                <Input
                  ref={inputRef}
                  autoFocus
                  className="bg-muted h-8 pe-11"
                  value={searchGroups}
                  onChange={updateSearch}
                  placeholder="Search groups..."
                  type="search"
                  aria-label="Search groups"
                  disabled={isLoading}
                  data-testid={TestId.WorkflowGroups.SubHeader.SearchInput}
                />
                <div className="text-muted-foreground pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-2">
                  <kbd className="text-muted-foreground/70 border-input bg-muted inline-flex h-5 max-h-full items-center rounded border px-1 font-[inherit] text-[0.625rem] font-medium">
                    ⌘F
                  </kbd>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row flex-nowrap items-center justify-end gap-3">
          {workflow && (
            <div className="text-muted-foreground text-xs font-medium">
              {groupsCount}
            </div>
          )}
          <Button
            onClick={openAddGroupModal}
            data-testid={TestId.WorkflowGroups.SubHeader.AddGroupButton}
          >
            Add a new group
          </Button>
        </div>
      </div>
    );
  }
);
